import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ExceptionManager } from "Core/logManager";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";

const INIT_STATE = {

  //createDriver
  driverResponse: null,
  loading: false,
  error: null,

  //searchDrivers
  entity: { id: "drivers" },
  loading: false,
  total: 0,
  filters: {
    tagIds: [], 
    unitIds: [], 
    entity: 'All', 
    includeInactiveDrivers: true, 
    unassigned: true 
  },
  registers: [],
  driverInfo:{},
  errorSearch: null,
  openFilters: false,

  //openCreateEdit
  openCreateEdit: false,
  driverGroup: null,
  supervisor: [],
  loadingSupervisor: false,
  errorSupervisor: null,
};

const route = "modules/settings/views/driver/redux/slice";

const KEY_MANAGE_REQUEST = "key_manage_request";

export const searchDrivers = createAsyncThunk(
  "settings/driver/searchDrivers",
  async (payload, { rejectWithValue, dispatch }) => {
    const { conditions, sort, limit = 10, offset = 0 } = payload;

    if(conditions?.length === 0 && !sort?.field && !sort?.order) return KEY_MANAGE_REQUEST

    try {
      let data = await clientQuery(
        `
        query searchDrivers ($conditions: [DriverSearchInput], $sort: [DriverSortInput], $limit: Int, $offset: Int) {
        searchDrivers (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
            items {
                driverId
                externalId
                name
                idTimeZone
                tagIds
                status
                licenseNumber
                licenseCountry
                licenseState
                driverPin
                hosRulesetIds
                hosExempt
                hosExemptComment
                email
                phone
                notifyByEmail
                notifyBySms
                supervisorId
                supervisor {
                    firstName
                    lastName
                }
                fsmDriver {
                    username
                }
                keyfob {
                    hex
                }
                avatarFileId
                tempAvatarUrl
                avatarFile {
                    id
                    repositoryId
                    repositoryType
                    repositoryCode
                    repositoryPath
                    fileKey
                    originalName
                }
                unit {
                   id
                }
            }
            total
        }
    }
    `,
        {
          conditions,
          sort,
          limit,
          offset,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        const {
          searchDrivers: { items, total },
        } = data;
        return { items, total };
      } else {
        throw "Error while getting drivers";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "searchDrivers");
      rejectWithValue(exc);
    }
  },
  {
    condition: (payload, { getState }) => {
      const { driverTableSliceRedux } = getState();
      return !driverTableSliceRedux.loading;
    },
  }
);

export const searchSupervisor = createAsyncThunk(
  "settings/driver/searchSupervisor",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      let data = await clientQuery(
        `
          query searchSubUsers ($conditions: [SubUserSearchInput], $sort: [SubUserSortInput], $limit: Int, $offset: Int) {
            response: searchSubUsers (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
              items {
                id
                firstName
                lastName
                userName
                isSupervisor
              }
              total
            }
          }
        `,
        {},
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        const filter = data?.response?.items?.filter(
          (item) => item?.isSupervisor === true
        );
        const createFullname = filter?.map((subUser) => {
          return {
            ...subUser,
            fullname: `${subUser?.firstName} ${subUser?.lastName}`,
          };
        });

        return createFullname;
      } else {
        throw "Error while getting supervisor users";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "searchSupervisor");
      rejectWithValue(exc);
    }
  },
  {
    condition: (payload, { getState }) => {
      const { driverTableSliceRedux } = getState();
      return !driverTableSliceRedux.loadingSupervisor;
    },
  }
);

export const driverTableSliceRedux = createSlice({
  name: "driver/driverTable",
  initialState: INIT_STATE,
  reducers: {
    toggleDriversFilters: (state, action) => {
      state.openFilters = action.payload;
    },
    manageFiltersDrivers: (state, action) => {
      state.filters = action.payload;
    }
  },
  extraReducers: (builder) => {
    //searchDrivers
    builder.addCase(searchDrivers.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(searchDrivers.fulfilled, (state, { payload }) => {
      state.loading = false;
      if(payload === KEY_MANAGE_REQUEST) return;
      state.registers = payload?.items;
      state.total = payload?.total;
    });
    builder.addCase(searchDrivers.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    //searchSupervisor
    builder.addCase(searchSupervisor.pending, (state, action) => {
      state.loadingSupervisor = true;
    });
    builder.addCase(searchSupervisor.fulfilled, (state, { payload }) => {
      state.loadingSupervisor = false;
      state.supervisor = payload;
    });
    builder.addCase(searchSupervisor.rejected, (state, { payload }) => {
      state.loadingSupervisor = false;
      state.errorSupervisor = payload;
    });
  },
});

const { actions, reducer } = driverTableSliceRedux;

export const { openDriverCreateEditModal, manageFiltersDrivers, toggleDriversFilters } = actions;

export default reducer;
